import EditIcon from '@assets/icons/edit.svg';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import TrashIconSmall from '@assets/icons/trash-small.svg';
import { ActionButton } from '@/components/action-button';
import {
  calculatePortionCostInMicros,
  MenuItemIngredient,
  menuItemIngredientEditable,
  useMenuInfoContext,
} from '@/contexts/menu-info-context';
import {
  convertToMenuItemIngredientFormState,
  IngredientActions,
  MenuItemIngredientFormState,
} from './menu-item-ingredient-form';
import { createStyles } from '@/contexts/theme-context';
import {
  displayCurrencyPlaceholder,
  formatCurrency,
  MICRO_CONVERSION,
} from '@/utils/money';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { INPUT_HEIGHT } from '@/components/override-input';
import { ItemSubtitle } from '@/components/item-subtitle';
import { ListOption } from '@/components/list-item';
import { MenuItemIngredientActionSheet } from './menu-item-ingredient-action-sheet';
import { OrderableItemListItemImage } from '@/components/orders/orderable-item-list-item-image';
import {
  PACK_UNIT_TO_UNIT_TYPE_MAP,
  PortionUnit,
  UNIT_TYPE_TO_PORTION_UNIT_MAP,
  UnitType,
} from '@/utils/menu';
import { PackUnit } from '@/enums';
import { Picker } from '@/components/picker';
import { Platform, Text, View } from 'react-native';
import { TextInput } from '@/components/text-input';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

const QUANTITY_INPUT_WIDTH = 60;
const UNIT_INPUT_WIDTH = 140;
const ACTION_BUTTON_CONTAINER_WIDTH = 92;

export type MenuItemIngredientOption = ListOption<MenuItemIngredient>;

interface Props {
  menuItemUUID: string;
  option: MenuItemIngredientOption;
}

export const MenuItemIngredientPricing: React.FC<Props> = ({
  menuItemUUID,
  option,
}) => {
  const [styles] = useStyles();
  const { isLargeLayout } = useLayoutContext();
  const { currencyCode } = useVariantPackInfoContext();
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const {
    editMenuItemIngredient,
    removeMenuItemIngredient,
    setMenuItemIngredientPortion,
  } = useMenuInfoContext();
  const [showIngredientForm, setShowIngredientForm] = useState(false);

  const ingredient = option.metadata!;
  const packUnit = ingredient.basePackUnit ?? ingredient.packUnit;
  const unitType = PACK_UNIT_TO_UNIT_TYPE_MAP[packUnit] as UnitType;

  const portionQuantity = ingredient.portionQuantity;
  const [portionQuantityText, setPortionQuantityText] = useState<string | null>(
    portionQuantity ? String(portionQuantity) : null,
  );

  const portionUnitOptions =
    UNIT_TYPE_TO_PORTION_UNIT_MAP[unitType] ?? Object.values(PortionUnit);

  const portionUnit =
    ingredient.portionUnit ??
    (portionUnitOptions.includes(PortionUnit.Each)
      ? PortionUnit.Each
      : (portionUnitOptions?.[0] ?? null));

  const calculatedPortionCostInMicros =
    calculatePortionCostInMicros(ingredient);

  const onQuantityChange = (value: string) => {
    const trimmedValue = value.trim();
    const parsedValue = Number(trimmedValue);
    const isNumber = !isNaN(parsedValue);

    // Allow decimals without leading 0
    if (isNumber || trimmedValue === '.') {
      setPortionQuantityText(trimmedValue);
      if (isNumber) {
        setMenuItemIngredientPortion(
          {
            portionQuantity: parsedValue,
            portionUnit,
          },
          ingredient.ingredientMetadataUUID,
          menuItemUUID,
        );
      }
    } else {
      setPortionQuantityText('');
    }
  };

  const handleFormSubmit = (formState: MenuItemIngredientFormState) => {
    editMenuItemIngredient(ingredient.ingredientUUID, {
      base_unit:
        formState.packUnit !== PackUnit.Case ? '' : formState.basePackUnit,
      cost_in_micros: Number(formState.ingredientPrice) * MICRO_CONVERSION,
      ingredient_name: formState.ingredientName,
      pack_unit: formState.packUnit,
      unit_count:
        formState.packUnit === PackUnit.Case ? formState.baseUnitCount : 1,
    });
    setShowIngredientForm(false);
  };

  const isEditable = menuItemIngredientEditable(ingredient);

  return (
    <>
      <View style={[styles.base, isLargeLayout ? styles.baseLarge : null]}>
        <View style={styles.row}>
          {ingredient.photoURLs?.length ? (
            <View style={styles.image}>
              <OrderableItemListItemImage
                alt={ingredient.ingredientName}
                hidePlaceholderImages={hidePlaceholderImages}
                imageURLs={ingredient.photoURLs}
              />
            </View>
          ) : null}
          <View style={styles.description}>
            <Text numberOfLines={2} style={TYPOGRAPHY.pSemiBold}>
              {ingredient.ingredientName}
            </Text>
            <ItemSubtitle
              baseUnit={ingredient.basePackUnit}
              currencyCode={null}
              displayString={ingredient.packUnit}
              modifierString={(ingredient.basePackUnit
                ? `${ingredient.unitCount} ${ingredient.basePackUnit}`
                : ''
              ).toLowerCase()}
              showInlineModifiers
              sku={ingredient.externalItemUUID}
              unitCount={ingredient.unitCount}
              unitPriceInMicros={ingredient.costInMicros}
            />
          </View>
        </View>
        <View style={styles.row}>
          <View
            style={[
              styles.pricingContainer,
              isLargeLayout ? null : styles.pricingContainerSmall,
            ]}
          >
            <View style={styles.pricingRow}>
              <Text style={styles.pricingText}>{i18n.t('portion')}</Text>
              <Text style={styles.pricingText}>{i18n.t('portion_cost')}</Text>
            </View>
            <View style={styles.pricingRow}>
              <View style={styles.pricingInputs}>
                <TextInput
                  containerStyle={[
                    styles.inputContainer,
                    styles.quantityContainer,
                  ]}
                  keyboardType="decimal-pad"
                  onChangeText={onQuantityChange}
                  showDoneButtonAccessoryView
                  style={[
                    TYPOGRAPHY.small,
                    FONTS.primaryBold,
                    styles.quantityInput,
                  ]}
                  value={portionQuantityText ?? ''}
                />
                <Picker
                  onSelect={(value) => {
                    const newPortionUnit = value as PortionUnit;
                    setMenuItemIngredientPortion(
                      {
                        portionQuantity,
                        portionUnit: newPortionUnit,
                      },
                      ingredient.ingredientMetadataUUID,
                      menuItemUUID,
                    );
                  }}
                  options={portionUnitOptions}
                  selectedValue={portionUnit}
                  style={styles.unitContainer}
                />
              </View>
              <Text style={TYPOGRAPHY.h3Bold}>
                {calculatedPortionCostInMicros > 0
                  ? formatCurrency(calculatedPortionCostInMicros, currencyCode)
                  : displayCurrencyPlaceholder(currencyCode)}
              </Text>
            </View>
          </View>
          {Platform.OS === 'web' ? (
            <View style={styles.actionButtonContainer}>
              <ActionButton
                Icon={TrashIconSmall}
                iconProps={{
                  height: 23,
                  width: 20,
                }}
                onPress={() => {
                  removeMenuItemIngredient(
                    ingredient.ingredientMetadataUUID,
                    menuItemUUID,
                  );
                }}
                style={styles.actionButton}
              />
              {isEditable ? (
                <ActionButton
                  Icon={EditIcon}
                  onPress={() => {
                    setShowIngredientForm(true);
                  }}
                  style={styles.actionButton}
                />
              ) : null}
            </View>
          ) : null}
        </View>
      </View>
      {showIngredientForm ? (
        <MenuItemIngredientActionSheet
          action={IngredientActions.EDIT}
          ingredient={convertToMenuItemIngredientFormState(option.metadata!)}
          onSubmit={handleFormSubmit}
          originalBaseUnit={ingredient.originalBaseUnit}
          setShowIngredientForm={setShowIngredientForm}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  actionButton: {
    alignSelf: 'center',
    padding: 12,
  },
  actionButtonContainer: {
    flexDirection: 'row',
    width: ACTION_BUTTON_CONTAINER_WIDTH,
  },
  base: {
    flex: 1,
    paddingTop: 4,
  },
  baseLarge: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  description: {
    flex: 1,
    flexDirection: 'column',
  },
  image: {
    marginLeft: 8,
    marginRight: 16,
  },
  inputContainer: {
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.stroke,
    borderRadius: 4,
    borderWidth: 2,
    height: INPUT_HEIGHT,
  },
  pricingContainer: {
    backgroundColor: theme.colors.blueAction,
    borderRadius: 4,
    flex: 1,
    justifyContent: 'space-between',
    padding: 8,
  },
  pricingContainerSmall: {
    marginTop: 8,
  },
  pricingInputs: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
  },
  pricingRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  pricingText: {
    ...FONTS.primaryBold,
    color: theme.colors.blueActionHeader,
    fontSize: 12,
    textTransform: 'uppercase',
  },
  quantityContainer: {
    width: QUANTITY_INPUT_WIDTH,
  },
  quantityInput: {
    paddingHorizontal: 4,
    textAlign: 'center',
  },
  row: {
    flex: 1,
    flexDirection: 'row',
  },
  unitContainer: {
    paddingVertical: 0,
    width: UNIT_INPUT_WIDTH,
  },
}));
